import { api, type ApiError, type ComponentItem } from '../api/client.js';
import { copyTextWithFeedback } from '../util/copy-feedback.js';

export interface AddComponentResult {
  component: ComponentItem;
  token: string;
}

/**
 * Opens the "Add new" modal. Resolves with the created component + token,
 * or `null` if the user dismissed the dialog.
 */
export function openAddComponentModal(): Promise<AddComponentResult | null> {
  return new Promise((resolve) => {
    const dialog = document.createElement('sl-dialog');
    dialog.label = 'New custom component';
    dialog.innerHTML = `
      <form class="field-stack" id="cc-form">
        <sl-input id="cc-name" name="name" label="Component name" required maxlength="120"></sl-input>
        <sl-textarea id="cc-description" name="description" label="Description" rows="3" maxlength="2000"></sl-textarea>
        <sl-alert id="cc-error" variant="danger" closable></sl-alert>
      </form>

      <div id="cc-result" style="display:none">
        <sl-alert variant="success" open>
          <sl-icon slot="icon" name="check2-circle"></sl-icon>
          Copy the token and use it in sa-tools CLI.
        </sl-alert>
        <div class="field-stack" style="margin-top:0.75rem">
          <label><strong>Publish token</strong></label>
          <div class="token-readout">
            <code id="cc-token-text"></code>
            <sl-icon-button id="cc-copy" class="copy-feedback" name="clipboard" label="Copy token"></sl-icon-button>
          </div>
          <small>This is shown only once. Store it somewhere safe.</small>
        </div>
      </div>

      <sl-button slot="footer" id="cc-cancel" variant="default">Cancel</sl-button>
      <sl-button slot="footer" id="cc-create" variant="primary">Create</sl-button>
      <sl-button slot="footer" id="cc-done" variant="primary" style="display:none">Done</sl-button>
    `;

    document.body.appendChild(dialog);

    const form = dialog.querySelector<HTMLFormElement>('#cc-form')!;
    const result = dialog.querySelector<HTMLElement>('#cc-result')!;
    const errorEl = dialog.querySelector<HTMLElement & { open: boolean }>('#cc-error')!;
    const nameEl = dialog.querySelector<HTMLInputElement>('#cc-name')!;
    const descEl = dialog.querySelector<HTMLInputElement>('#cc-description')!;
    const tokenText = dialog.querySelector<HTMLElement>('#cc-token-text')!;
    const cancelBtn = dialog.querySelector<HTMLElement>('#cc-cancel')!;
    const createBtn = dialog.querySelector<HTMLElement>('#cc-create')!;
    const doneBtn = dialog.querySelector<HTMLElement>('#cc-done')!;
    const copyBtn = dialog.querySelector<HTMLElement>('#cc-copy')!;

    const showError = (msg: string) => {
      errorEl.textContent = msg;
      errorEl.open = true;
    };
    const hideError = () => {
      errorEl.open = false;
    };

    let created: AddComponentResult | null = null;

    const cleanup = () => {
      dialog.remove();
      resolve(created);
    };

    cancelBtn.addEventListener('click', () => dialog.hide());
    doneBtn.addEventListener('click', () => dialog.hide());
    dialog.addEventListener('sl-after-hide', cleanup);

    createBtn.addEventListener('click', async () => {
      hideError();
      const name = nameEl.value.trim();
      if (!name) {
        showError('Component name is required');
        return;
      }
      const loadingBtn = createBtn as HTMLButtonElement & { loading: boolean };
      try {
        loadingBtn.loading = true;
        const res = await api.createComponent({
          name,
          description: descEl.value.trim(),
        });
        created = res;
        tokenText.textContent = res.token;

        form.style.display = 'none';
        result.style.display = '';
        createBtn.style.display = 'none';
        cancelBtn.style.display = 'none';
        doneBtn.style.display = '';
      } catch (err) {
        console.error('[add-component] failed', err);
        const apiErr = err as ApiError;
        showError(apiErr?.message ?? 'Failed to create component');
      } finally {
        loadingBtn.loading = false;
      }
    });

    copyBtn.addEventListener('click', async () => {
      if (!created) return;
      await copyTextWithFeedback(created.token, copyBtn, { copiedLabel: 'Copied token' });
    });

    requestAnimationFrame(() => {
      (dialog as HTMLElement & { show: () => void }).show();
    });
  });
}
